import cn from 'classnames';
import { format } from 'date-fns';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ReactComponent as MailIcon } from '../../assets/images/icon-input-mail.svg';
import { ReactComponent as PYGIcon } from '../../assets/images/icon-pick-your-gift.svg';
import {
  DELAYED_SHIPPING_EXPIRATION,
  EXPERIENCE_ID,
  getDefaultExpirationDateInUTC,
  PRE_CREATED_ENGAGEMENT_ID,
  RECEIVER_REMINDER_INTERVAL,
} from '../../constants/bucket';
import { DateFormatsEnum } from '../../constants/date';
import { BUSINESS_REASON_ID } from '../../constants/reasons';
import {
  GIFT_BUDGET_SUMMARY_LABEL,
  GIFT_BUDGET_SUMMARY_NOT_USED,
  GIFT_BUDGET_SUMMARY_TOOLTIP,
  MODERN_DROPSHIP_ORDER_NOTE,
} from '../../constants/reports';
import { endpoints } from '../../constants/routing';
import {
  DISABLE_EMAIL_NOTIFICATIONS,
  DISABLE_RECIPIENT_EMAIL_NOTIFICATIONS_HINT,
  DISABLE_RECIPIENT_SHIPPED_EMAIL,
  DISABLE_SENDER_EMAIL_NOTIFICATIONS,
  DISABLE_SENDER_EMAIL_NOTIFICATIONS_HINT,
} from '../../constants/shipping';
import {
  CLAIM_LINK_TEMPLATE_CARD_MAX_WIDTH,
  EMAIL_MESSAGE,
  EMAIL_SUBJECT,
  MESSAGE,
  RECEIVER_EMAIL,
} from '../../constants/templates';
import { SUPER_ADMIN } from '../../constants/users';
import AdditionalSummaryContainer from '../../containers/AdditionalSummaryContainer/AdditionalSummaryContainer';
import ReportsShippingDetailsContainer from '../../containers/ShippingDetailsContainer/Reports/Reports';
import SendShippingDetailsSummaryForm from '../../containers/ShippingDetailsContainer/SingleSend/SummaryWidget';
import { useClaimLinkTemplate } from '../../hooks/useClaimLinkTemplate';
import useFetch from '../../hooks/useFetch';
import { addBucketValue } from '../../store/actions/bucket';
import { editEngagementRequest } from '../../store/actions/engagement';
import { updateReportValue } from '../../store/actions/reports';
import { selectAdminType } from '../../store/selectors/auth';
import {
  selectIsBusinessReasonRequired,
  selectIsDisabledRecipientEmailNotifications,
  selectIsDisabledSenderNotifications,
  selectIsReceiverReminderSettingEnabled,
  selectReminderInterval,
} from '../../store/selectors/bucket';
import { selectFulfillmentCenterById } from '../../store/selectors/fulfillmentCenters';
import { selectIsProductMarketplaceEnabled } from '../../store/selectors/organizations';
import { IEditSendFieldRequestPayload, TEngagementCandidate } from '../../types/bucket';
import { ICampaign } from '../../types/campaigns';
import { IOnDemandItem } from '../../types/inventories';
import {
  EditSendModeEnum,
  GiftBudgetUsageKindEnum,
  IReport,
  IReportWithCampaignSummaries,
  ReportStatusEnum,
  ReportTypesEnum,
} from '../../types/reports';
import { IReportsRouteParams } from '../../types/routing';
import {
  CopyableLinkTypesEnum,
  IApiError,
  InventorySidebarTypeEnum,
  IResultPriceWithCurrency,
  NotificationListEnum,
} from '../../types/shell';
import { IBulkPayloadProps } from '../../types/shipping';
import { IClaimLinkTemplate } from '../../types/templates';
import { formatDate, formatUtcDateForDisplayInUserTimezone, getUTCDate, isValidDate } from '../../utils/date';
import { hasMskuItem, isItemOnDemand } from '../../utils/inventories';
import { getMarketplaceSendCards, getWarehouseItems, offersMarketplaceContent } from '../../utils/marketplaceSend';
import notification from '../../utils/notification';
import {
  formatGiftBudgetSpentOf,
  getReportCopyableInfo,
  getReportGiftBudgetUsage,
  getReportItemSources,
  getReportStatusAndDate,
  getReportWarehouseNames,
  hasMarketplaceItemSource,
  isAwaitingAddress,
  isAwaitingShipment,
  isDelivered,
  isDirectSendEngagement,
  isReportViewed,
  isShipped,
  prepareShippingRequestData,
  transformReportAddressToCommonAddress,
} from '../../utils/reports';
import { hasPermission } from '../../utils/users';
import DisableEmailNotifications from '../DisableEmailNotifications/DisableEmailNotifications';
import {
  ActionButton,
  CopyableButton,
  DateAndTimezonePicker,
  Input,
  InputLabel,
  IntervalInput,
  LabelWithEditButton,
  ReportsCopyableButton,
  Textarea,
} from '../forms';
import { HelpTooltip, HtmlView, Price, ReportsStatus } from '../index';
import InventorySidebar from '../InventorySidebar/InventorySidebar';
import ReportItemSourceDetails from '../ReportsComponent/components/ReportItemSourceDetails';
import { ClaimLinkTemplateCardBase } from '../TemplatesComponent';
import { ChooseClaimLinkTemplateModal } from '../_mui';
import RecipientDetails from './RecipientDetails/RecipientDetails';

import styles from './SummaryComponent.module.scss';

interface ICommonProps {
  className?: string;
  instance?: TEngagementCandidate | IReportWithCampaignSummaries | null;
  showNote: boolean;
  showEmail: boolean;
  total: IResultPriceWithCurrency;
  isDigital: boolean;
  reportMode: boolean;
  isPYG?: boolean;
  showQuantity?: boolean;
  isEditable?: boolean;
  onEdit?: (mode: EditSendModeEnum) => void;
  onReorderCampaigns?: (next: ICampaign[]) => void;
}

interface IEngagementFlowProps extends ICommonProps {
  SenderInfo: React.ReactNode;
  onSubmit: (...args: any[]) => void;
  type?: never;
}

interface IReportProps extends ICommonProps {
  SenderInfo?: never;
  onSubmit?: never;
  type: ReportTypesEnum;
}

export type TSendSummaryProps = IEngagementFlowProps | IReportProps;

const SendSummaryComponent: React.FC<TSendSummaryProps> = ({
  className,
  instance,
  showNote,
  showEmail,
  total,
  isDigital,
  SenderInfo,
  reportMode,
  isPYG,
  isEditable,
  showQuantity,
  onSubmit,
  onEdit,
  onReorderCampaigns,
  type = ReportTypesEnum.Regular,
}) => {
  const dispatch = useDispatch();

  const { reportId } = useParams<IReportsRouteParams>();

  const receiverReminderInterval = useSelector(selectReminderInterval);
  const isReceiverReminderSettingEnabled = useSelector(selectIsReceiverReminderSettingEnabled);
  const isBusinessReasonRequired = useSelector(selectIsBusinessReasonRequired);
  const isDisabledSenderEmailNotifications = useSelector(selectIsDisabledSenderNotifications);
  const isDisabledRecipientEmailNotifications = useSelector(selectIsDisabledRecipientEmailNotifications);
  const isDisabled = isBusinessReasonRequired && !instance?.[BUSINESS_REASON_ID];
  const adminType = useSelector(selectAdminType);

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isShippingReadOnly, setIsShippingReadOnly] = useState<boolean>(true);
  const [isShippingDetailsLoading, setIsShippingDetailsLoading] = useState<boolean>(false);

  const isDelayedShipping = useMemo(() => {
    return isAwaitingAddress(instance as IReportWithCampaignSummaries);
  }, [instance]);

  const shipOrderStatus = useMemo(() => {
    return isDelayedShipping || instance?.delayed_shipping_url ? ReportStatusEnum.AwaitingAddress : '';
  }, [isDelayedShipping, instance?.delayed_shipping_url]);

  const initialShippingValues = useMemo(() => {
    if (!instance) {
      return null;
    }

    return {
      status: shipOrderStatus,
      is_receiver_address_fixed: instance.is_receiver_address_fixed,
      org_address_ids: instance.org_address_ids,
      receiver_address: transformReportAddressToCommonAddress(instance),
      // yup infers the schema field as Maybe<boolean>; the consumer prop is boolean | undefined
      is_office_dropship_enabled: instance.is_office_dropship_enabled ?? undefined,
    };
  }, [instance, shipOrderStatus]);

  const { make: editSendFieldRequest, isLoading: isExpirationDataChanging } = useFetch<
    IEditSendFieldRequestPayload,
    never
  >({
    endpoint: endpoints.editSendField,
    showToastOnError: true,
    method: 'PATCH',
    useMonolithApi: true,
  });

  const warehouseItems = useMemo(() => getWarehouseItems(instance?.items), [instance?.items]);
  const fulfillmentCenterId = useMemo(() => warehouseItems[0]?.fulfillment_center_id, [warehouseItems]);
  const fulfillmentCenter = useSelector(selectFulfillmentCenterById(fulfillmentCenterId));
  const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);

  const isItemsEditDisabledByFC = useMemo(() => {
    return fulfillmentCenter?.disable_send_items_edit;
  }, [fulfillmentCenter]);

  const hasMsku = useMemo(() => hasMskuItem(instance?.items), [instance]);

  const isSuperAdmin = useMemo(() => hasPermission([SUPER_ADMIN], adminType), [adminType]);
  const isSendShipped = useMemo(() => instance && isShipped(instance as IReport), [instance]);
  const isSendDelivered = useMemo(() => instance && isDelivered(instance as IReport), [instance]);

  const isOnHoldUntilDate = useMemo(
    () => instance?.on_hold_until && isValidDate(new Date(instance.on_hold_until)),
    [instance],
  );
  const isViewedDate = useMemo(
    () => instance?.delayed_shipping_page_view_date && isValidDate(new Date(instance.delayed_shipping_page_view_date)),
    [instance],
  );
  const isNextReminderDate = useMemo(
    () =>
      instance?.receiver_next_reminder_send_date && isValidDate(new Date(instance.receiver_next_reminder_send_date)),
    [instance],
  );

  const shipOrderId = useMemo(
    () =>
      (instance as IReportWithCampaignSummaries)?.ship_order_id ||
      (instance as IReportWithCampaignSummaries)?.ship_order_id_str,
    [instance],
  );

  const areMarketplaceFieldsShown = useMemo(
    () => reportMode && isMarketplaceEnabled && type === ReportTypesEnum.Regular,
    [reportMode, isMarketplaceEnabled, type],
  );

  const itemSources = useMemo(() => getReportItemSources(instance as IReportWithCampaignSummaries), [instance]);

  const warehouseNames = useMemo(() => getReportWarehouseNames(instance as IReportWithCampaignSummaries), [instance]);

  const isMarketplaceSend = useMemo(
    () => hasMarketplaceItemSource(instance as IReportWithCampaignSummaries),
    [instance],
  );

  const marketplaceOrderNumber = useMemo(
    () => (isMarketplaceSend ? (instance as IReportWithCampaignSummaries)?.ship_order_id_str : undefined),
    [instance, isMarketplaceSend],
  );

  const isMarketplaceOrderShown = areMarketplaceFieldsShown && isSuperAdmin && !!marketplaceOrderNumber;

  const giftBudgetUsage = useMemo(
    () => getReportGiftBudgetUsage(instance as IReportWithCampaignSummaries | null | undefined),
    [instance],
  );
  const isGiftBudgetShown =
    areMarketplaceFieldsShown && !!giftBudgetUsage && giftBudgetUsage.kind !== GiftBudgetUsageKindEnum.Pending;

  const {
    date: dateByStatus,
    label: status,
    color: statusColor,
    dateLabel,
  } = useMemo(() => {
    if (reportMode && instance) {
      return getReportStatusAndDate(instance as IReportWithCampaignSummaries);
    }
    return {
      date: '',
      label: '',
      color: '',
      dateLabel: '',
    };
  }, [instance, reportMode]);

  const additionalForm = useMemo(() => {
    return (
      <AdditionalSummaryContainer
        viewMode={reportMode}
        engagement={instance}
        sendId={reportId}
        isSendEditable={isEditable}
        isOneLink={!!instance?.one_link_id}
      />
    );
  }, [reportMode, instance, adminType, reportId]);

  const emailInfo = useMemo(() => {
    if (!(instance && showEmail)) {
      return null;
    }

    return (
      <div className={styles.emailInfo}>
        <div className={styles.emailTop}>
          <Input
            className={styles.emailInput}
            helperText="Email"
            icon={<MailIcon />}
            value={instance[RECEIVER_EMAIL]}
            readOnly
          />
          <Input className={styles.emailInput} helperText="Subject" value={instance[EMAIL_SUBJECT]} readOnly />
        </div>
        <div className={styles.emailBottom}>
          <InputLabel value="Email text" />
          <HtmlView
            containerClassname={cn(styles.textareaContainer, styles.htmlViewContainer)}
            html={instance[EMAIL_MESSAGE]!}
          />
        </div>
      </div>
    );
  }, [showEmail, instance]);

  const noteArea = useMemo(() => {
    if (!(instance && showNote)) {
      return null;
    }
    return (
      <Textarea
        helperText={
          <LabelWithEditButton
            isEditable={isEditable}
            text="Custom message text"
            onEdit={() => onEdit?.(EditSendModeEnum.Details)}
          />
        }
        className={styles.textareaContainer}
        inputClassName={styles.noteTextarea}
        value={instance[MESSAGE]}
        readOnly
      />
    );
  }, [showNote, instance, isEditable, onEdit]);

  const selectedClaimPageTemplate = useClaimLinkTemplate(instance?.experience_id);

  const isDirectSend = useMemo(
    () => isDirectSendEngagement({ engagement_source_type: (instance as IReport)?.engagement_source_type }),
    [instance],
  );

  const handleOpenTemplateModal = useCallback(() => {
    setIsTemplateModalOpen(true);
  }, []);

  const handleCloseTemplateModal = useCallback(() => {
    setIsTemplateModalOpen(false);
  }, []);

  const handleSelectExperience = useCallback(
    (template: IClaimLinkTemplate) => {
      if (!reportId) {
        return;
      }
      editSendFieldRequest({
        pathParams: { id: reportId },
        body: { key: EXPERIENCE_ID, value: template.uid },
      }).then(() => {
        dispatch(updateReportValue({ experience_id: template.uid, id: reportId, type }));
        notification.success(NotificationListEnum.Success, { content: 'Claim page template has been updated' });
      });
    },
    [reportId, type, editSendFieldRequest, dispatch],
  );

  const claimPageTemplateArea = useMemo(() => {
    if (isDigital || isDirectSend) {
      return null;
    }

    const isExperienceEditableStatus =
      instance?.status === ReportStatusEnum.AwaitingAddress || instance?.status === ReportStatusEnum.Viewed;
    const canEdit = reportMode && isEditable && isExperienceEditableStatus;

    return (
      <div style={{ maxWidth: CLAIM_LINK_TEMPLATE_CARD_MAX_WIDTH }}>
        {canEdit ? (
          <LabelWithEditButton
            isEditable
            text="Claim page template"
            onEdit={handleOpenTemplateModal}
            dataAction="edit_claim_page_template_btn"
          />
        ) : (
          <InputLabel value="Claim page template" />
        )}
        <ClaimLinkTemplateCardBase
          title={selectedClaimPageTemplate?.name ?? 'No template selected'}
          thumbnailUrl={selectedClaimPageTemplate?.cover_image_url}
          status={selectedClaimPageTemplate?.status}
          updatedAt={selectedClaimPageTemplate ? selectedClaimPageTemplate.updated_at : undefined}
          config={{ showStatusChip: false }}
        />
        {canEdit && (
          <ChooseClaimLinkTemplateModal
            isOpen={isTemplateModalOpen}
            onClose={handleCloseTemplateModal}
            onSelect={handleSelectExperience}
            currentTemplateId={instance?.experience_id}
            departmentId={(instance as IReportWithCampaignSummaries)?.department_id}
          />
        )}
      </div>
    );
  }, [
    isDigital,
    isDirectSend,
    reportMode,
    isEditable,
    instance?.status,
    selectedClaimPageTemplate,
    isTemplateModalOpen,
    instance?.experience_id,
    handleOpenTemplateModal,
    handleCloseTemplateModal,
    handleSelectExperience,
  ]);

  const orderIdsBar = useMemo(() => {
    return reportMode ? (
      <div className={styles.sectionBar}>
        <div className={styles.infoBar}>
          <InputLabel value="Order ID" />
          <CopyableButton
            inline
            value={(instance as IReportWithCampaignSummaries)?.order_id}
            className={styles.orderId}
            toastLabel="Order ID"
          >
            {(instance as IReportWithCampaignSummaries)?.order_id}
          </CopyableButton>
        </div>
        {(instance as IReportWithCampaignSummaries)?.woocommerce_order_id ? (
          <div className={styles.infoBar}>
            <InputLabel value="WooCommerce ID" containerClassName={styles.idLabel} />
            <CopyableButton
              inline
              value={(instance as IReportWithCampaignSummaries).woocommerce_order_id || ''}
              className={styles.copyableButton}
              toastLabel="WooCommerce ID"
            >
              {(instance as IReportWithCampaignSummaries)?.woocommerce_order_id}
            </CopyableButton>
          </div>
        ) : null}
        {isSuperAdmin && shipOrderId && !(isMarketplaceOrderShown && shipOrderId === marketplaceOrderNumber) ? (
          <div className={styles.infoBar}>
            <InputLabel value="Shipment ID" />
            <CopyableButton inline value={shipOrderId || ''} className={styles.orderId} toastLabel="Shipment ID">
              {shipOrderId}
            </CopyableButton>
          </div>
        ) : null}
        {isMarketplaceOrderShown ? (
          <div className={styles.infoBar}>
            <InputLabel value="Shipment ID" />
            <CopyableButton inline value={marketplaceOrderNumber} className={styles.orderId} toastLabel="Shipment ID">
              {marketplaceOrderNumber}
            </CopyableButton>
            <span className={styles.idNote}>{MODERN_DROPSHIP_ORDER_NOTE}</span>
          </div>
        ) : null}
      </div>
    ) : null;
  }, [instance, reportMode, isSuperAdmin, shipOrderId, isMarketplaceOrderShown, marketplaceOrderNumber]);

  const statusBar = useMemo(() => {
    if (!instance || !(type === ReportTypesEnum.Regular || isOnHoldUntilDate)) {
      return null;
    }
    return (
      <div className={styles.sectionBar}>
        {type === ReportTypesEnum.Regular && (
          <div className={styles.infoBar}>
            <InputLabel value="Status" />
            <ReportsStatus className={styles.status} status={status} backgroundColor={statusColor} />
          </div>
        )}
        {isOnHoldUntilDate && (
          <div className={styles.infoBar}>
            <InputLabel value="On hold until" />
            <span className={styles.date}>{formatUtcDateForDisplayInUserTimezone(instance.on_hold_until!)}</span>
          </div>
        )}
        {areMarketplaceFieldsShown && (
          <div className={styles.infoBar}>
            <InputLabel value="Item source" />
            <ReportItemSourceDetails sources={itemSources} warehouseNames={warehouseNames} />
          </div>
        )}
      </div>
    );
  }, [instance, status, statusColor, isOnHoldUntilDate, type, areMarketplaceFieldsShown, itemSources, warehouseNames]);

  const handleNotificationChange = useCallback(
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      dispatch(addBucketValue({ [name]: checked }));
    },
    [dispatch],
  );

  const handleChangeExpirationDate = useCallback(
    (value: string) => {
      const isSameDate =
        instance?.delayed_shipping_expiration && value
          ? isEqual(
              getUTCDate(formatDate(instance.delayed_shipping_expiration, DateFormatsEnum.FullDateAndTime) as string),
              getUTCDate(formatDate(value, DateFormatsEnum.FullDateAndTime)),
            )
          : false;

      if (reportId && value && !isSameDate) {
        const report = instance as IReportWithCampaignSummaries;
        editSendFieldRequest({
          pathParams: {
            id: report?.uid,
          },
          body: {
            key: DELAYED_SHIPPING_EXPIRATION,
            value: formatDate(value, DateFormatsEnum.FullDateAndTime),
          },
        })
          .then(() => {
            dispatch(
              updateReportValue({
                [DELAYED_SHIPPING_EXPIRATION]: formatDate(value, DateFormatsEnum.FullDateAndTime),
                id: reportId,
                type,
              }),
            );
          })
          .then(() =>
            notification.success(NotificationListEnum.Success, { content: 'Expiration date has been updated' }),
          );
      }
    },
    [instance, editSendFieldRequest, type, dispatch, reportId],
  );

  const handleShippingInfoSubmit = useCallback(
    (shipping: IBulkPayloadProps) => {
      const preparedShipping = prepareShippingRequestData(shipping);

      return new Promise<IApiError | void>((resolve, reject) => {
        setIsShippingDetailsLoading(true);
        dispatch(
          editEngagementRequest({
            engagement: { shipping_info: preparedShipping, type, engagement_id: reportId! },
            resolve,
            reject,
          }),
        );
      })
        .then((response) => {
          const { message } = (response as IApiError) || {};
          if (message && message?.length > 0) {
            notification.warning(NotificationListEnum.EditEngagement, {
              content: message,
            });
          } else {
            notification.success(NotificationListEnum.EditEngagement, {
              content: 'The send was updated successfully!',
            });
          }
        })
        .catch((error) => {
          notification.error(NotificationListEnum.EditEngagement, {
            content: error?.message ? error.message : 'Something bad has happened. The send edits were not applied.',
          });
        })
        .finally(() => {
          setIsShippingReadOnly(true);
          setIsShippingDetailsLoading(false);
        });
    },
    [dispatch, type, reportId],
  );

  const datesBar = useMemo(() => {
    return (
      <div className={cn(styles.sectionBar, styles.datesColumn)}>
        <div className={styles.dateItem}>
          <InputLabel value="Created date" />
          <span className={styles.date}>{formatUtcDateForDisplayInUserTimezone(instance?.created_at!) || 'n/a'}</span>
        </div>
        {isViewedDate ? (
          <div className={styles.dateItem}>
            <InputLabel value={dateLabel} />
            <span className={styles.date}>
              {formatUtcDateForDisplayInUserTimezone(instance?.delayed_shipping_page_view_date!)}
            </span>
          </div>
        ) : null}
        {isDelayedShipping ? (
          <DateAndTimezonePicker
            className={cn(styles.dateItem, styles.datePickerButton)}
            onSubmit={(val) => {
              handleChangeExpirationDate(
                val?.selectedDate ? format(val.selectedDate, DateFormatsEnum.FullDateAndTime) : '',
              );
            }}
            value={instance?.delayed_shipping_expiration}
            isLoading={isExpirationDataChanging}
            defaultValue={getDefaultExpirationDateInUTC().toISOString()}
          />
        ) : null}
      </div>
    );
  }, [
    instance?.delayed_shipping_expiration,
    dateByStatus,
    dateLabel,
    handleChangeExpirationDate,
    isExpirationDataChanging,
    isDelayedShipping,
  ]);

  const reminderDateBar = useMemo(() => {
    return isNextReminderDate ? (
      <div className={styles.sectionBar}>
        <div className={styles.dateItem}>
          <InputLabel value="Next reminder date" />
          <span className={styles.date}>
            {formatUtcDateForDisplayInUserTimezone(instance?.receiver_next_reminder_send_date!)}
          </span>
        </div>
      </div>
    ) : null;
  }, [isNextReminderDate]);

  const linksList = useMemo(() => {
    if (!instance) {
      return null;
    }

    const copyableInfo = getReportCopyableInfo(instance as IReportWithCampaignSummaries);

    if (!copyableInfo || !copyableInfo.length) {
      return null;
    }

    const EngagementLink: React.FC<{ title: string; url: string }> = ({ title, url, ...restProp }) => {
      return (
        <div className={styles.link} {...restProp}>
          {title && <span className={styles.linkCopyTitle}>{title}</span>}
          <ReportsCopyableButton className={cn(styles.linkCopyButton)} value={url} toastLabel={'URL'} />
        </div>
      );
    };

    const CourierInfo = () => {
      return (
        <div className={cn(styles.sectionBar, styles.trackingContainer)}>
          <div>
            <InputLabel value="Tracking number" />
            {(instance as IReportWithCampaignSummaries).tracking_number ? (
              <CopyableButton
                inline
                value={(instance as IReportWithCampaignSummaries).tracking_number}
                className={styles.orderId}
                toastLabel="Tracking number"
              >
                {(instance as IReportWithCampaignSummaries).tracking_number}
              </CopyableButton>
            ) : (
              'n/a'
            )}
          </div>
          <div className={styles.courierContainer}>
            <InputLabel value="Courier" containerClassName={styles.idLabel} />
            {(instance as IReportWithCampaignSummaries).ship_carrier ? (
              <p className={styles.courier}>{(instance as IReportWithCampaignSummaries).ship_carrier}</p>
            ) : (
              'n/a'
            )}
          </div>
        </div>
      );
    };

    return (
      <div className={styles.infoBar}>
        {(isSendShipped || isSendDelivered) && <CourierInfo />}
        <InputLabel value="Links" />
        <div className={styles.linksContainer}>
          {copyableInfo.reverse().map(({ data, type: reportLinkType }) => {
            if (!reportMode && reportLinkType === CopyableLinkTypesEnum.DigitalGift) {
              return;
            }

            return Array.isArray(data) ? (
              data.map(({ label, url }) => <EngagementLink key={url} url={url} title={label} />)
            ) : (
              <EngagementLink key={data.url} url={data.url} title={data.label} />
            );
          })}
        </div>
      </div>
    );
  }, [instance, reportMode, isSendShipped, isSendDelivered]);

  const handleChangeReminderInterval = useCallback(
    (interval: string | null) => {
      dispatch(addBucketValue({ [RECEIVER_REMINDER_INTERVAL]: interval }));
    },
    [dispatch],
  );

  // Fetched items serialise `on_demand: false`, so check the value — isItemOnDemand() only checks the key.
  const isOnDemandItemExist = useMemo(
    () => instance?.items?.some((item) => isItemOnDemand(item) && (item as IOnDemandItem).on_demand !== false),
    [instance?.items],
  );
  const isForcedDS = useMemo(() => !!(isPYG || hasMsku), [isPYG, hasMsku]);

  const isPYGSidebar = useMemo(
    () => isPYG && !instance?.pre_created_engagement_id,
    [isPYG, instance?.pre_created_engagement_id],
  );

  const marketplaceSidebarItems = useMemo(
    () => getMarketplaceSendCards(instance as IReportWithCampaignSummaries | null | undefined, !!isPYGSidebar),
    [instance, isPYGSidebar],
  );

  const isPYGReorderableStatus = useMemo(() => {
    if (!reportMode) {
      return true;
    }
    if (!instance) {
      return false;
    }
    const report = instance as IReportWithCampaignSummaries;
    return isAwaitingAddress(report) || isReportViewed(report) || isAwaitingShipment(report);
  }, [reportMode, instance]);

  const offersMarketplace = useMemo(
    () => offersMarketplaceContent(instance as IReportWithCampaignSummaries | null | undefined),
    [instance],
  );

  const isInventorySidebarEditable = useMemo(() => {
    if (offersMarketplace) {
      return false;
    }

    return isPYGSidebar
      ? isEditable && isPYGReorderableStatus
      : isEditable && !isOnDemandItemExist && !isItemsEditDisabledByFC;
  }, [
    offersMarketplace,
    isPYGSidebar,
    isEditable,
    isPYGReorderableStatus,
    isOnDemandItemExist,
    isItemsEditDisabledByFC,
  ]);

  const inventorySidebarEditMode = isPYGSidebar ? EditSendModeEnum.PickCampaigns : EditSendModeEnum.Items;

  const isInProduction = useMemo(() => isAwaitingShipment(instance as IReportWithCampaignSummaries), [instance]);

  return (
    <div className={cn(styles.summary, className)}>
      <div className={styles.mainContainer}>
        {SenderInfo && <div className={styles.topContainer}>{SenderInfo}</div>}
        {instance && (
          <div className={styles.mainInfo}>
            <RecipientDetails
              instance={instance as IReportWithCampaignSummaries | TEngagementCandidate}
              isDigital={isDigital}
              isEditable={isEditable}
              onEdit={() => onEdit?.(EditSendModeEnum.Details)}
            />
          </div>
        )}
        {!isDigital && instance && (
          <div className={cn(styles.shipping, styles.mainInfo)}>
            {reportMode ? (
              <ReportsShippingDetailsContainer
                value={initialShippingValues}
                isEditable={isEditable}
                isReadOnly={isShippingReadOnly}
                onSubmit={handleShippingInfoSubmit}
                onEdit={() => setIsShippingReadOnly(false)}
                isForcedDS={isForcedDS}
                onCancel={() => setIsShippingReadOnly(true)}
                isLoading={isShippingDetailsLoading}
                isAvailableDS={!isInProduction}
              />
            ) : (
              <SendShippingDetailsSummaryForm instance={instance} />
            )}
          </div>
        )}
        {emailInfo}
        {noteArea}
      </div>
      {instance && (
        <div className={styles.sideContainer}>
          {isReceiverReminderSettingEnabled && !reportMode && (
            <IntervalInput
              value={receiverReminderInterval}
              onChange={handleChangeReminderInterval}
              className={styles.intervalInputContainer}
            />
          )}
          <DisableEmailNotifications
            label="Disable sender notifications"
            onChange={handleNotificationChange(DISABLE_SENDER_EMAIL_NOTIFICATIONS)}
            value={
              reportMode
                ? (instance as IReportWithCampaignSummaries)[DISABLE_EMAIL_NOTIFICATIONS]
                : isDisabledSenderEmailNotifications
            }
            isReadOnly={reportMode}
            hint={DISABLE_SENDER_EMAIL_NOTIFICATIONS_HINT}
          />
          {!isDigital && (
            <DisableEmailNotifications
              label="Disable recipient tracking email"
              onChange={handleNotificationChange(DISABLE_RECIPIENT_SHIPPED_EMAIL)}
              value={
                reportMode
                  ? (instance as IReportWithCampaignSummaries)[DISABLE_RECIPIENT_SHIPPED_EMAIL]
                  : isDisabledRecipientEmailNotifications
              }
              isReadOnly={reportMode}
              hint={DISABLE_RECIPIENT_EMAIL_NOTIFICATIONS_HINT}
            />
          )}
          {additionalForm}
          {reportMode && (
            <React.Fragment>
              {orderIdsBar}
              {statusBar}
              {datesBar}
              {reminderDateBar}
              {linksList}
            </React.Fragment>
          )}
          {claimPageTemplateArea}
          <InventorySidebar
            isEditable={isInventorySidebarEditable}
            onEdit={() => onEdit?.(inventorySidebarEditMode)}
            showCount={!reportMode && !showQuantity}
            showQuantity={showQuantity}
            label={
              reportMode ? (
                <div className={styles.pygInfo}>
                  <InputLabel className={styles.pygLabel} value="Send pack" />
                  {isPYG && <PYGIcon className={styles.pygIcon} />}
                </div>
              ) : (
                <InputLabel value="Your send" />
              )
            }
            reportMode={reportMode}
            selected={instance[PRE_CREATED_ENGAGEMENT_ID]}
            className={styles.inventorySidebar}
            items={isPYGSidebar ? instance.pick_campaigns : warehouseItems}
            marketplaceItems={marketplaceSidebarItems}
            type={isPYGSidebar ? InventorySidebarTypeEnum.Campaign : InventorySidebarTypeEnum.InventoryItem}
            onReorder={isPYGSidebar ? onReorderCampaigns : undefined}
          />
          {isGiftBudgetShown && giftBudgetUsage ? (
            <div className={styles.giftBudgetBar}>
              <div className={styles.infoBar}>
                <InputLabel
                  value={
                    <React.Fragment>
                      <span>{GIFT_BUDGET_SUMMARY_LABEL}</span>
                      <HelpTooltip
                        id="gift-budget-tooltip"
                        className={styles.giftBudgetTooltip}
                        contentClassName={styles.tooltipText}
                      >
                        {GIFT_BUDGET_SUMMARY_TOOLTIP}
                      </HelpTooltip>
                    </React.Fragment>
                  }
                />
                <span className={styles.giftBudgetValue} data-testid="gift-budget-usage">
                  {giftBudgetUsage.kind === GiftBudgetUsageKindEnum.Spent
                    ? formatGiftBudgetSpentOf(giftBudgetUsage)
                    : GIFT_BUDGET_SUMMARY_NOT_USED}
                </span>
              </div>
            </div>
          ) : null}
          <div className={styles.pricesBar}>
            <div className={styles.infoBar}>
              <InputLabel
                value={
                  !reportMode && isPYG ? (
                    <React.Fragment>
                      <span>Send cost</span>
                      <HelpTooltip
                        id="instance-cost-tooltip"
                        className={styles.tooltip}
                        contentClassName={styles.tooltipText}
                      >
                        You will be charged for the biggest price and when the user picks his gift the money will be
                        returned.
                      </HelpTooltip>
                    </React.Fragment>
                  ) : (
                    'Item(s) cost'
                  )
                }
              />
              <Price className={styles.price} value={total} />
            </div>
          </div>
          {!reportMode && (
            <ActionButton
              className={styles.actionButton}
              onClick={onSubmit}
              title="Create send"
              disabled={isDisabled}
              dataAction="create_send_btn"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SendSummaryComponent;
